import { ChangeDetectionStrategy, Component, OnInit, inject, input } from '@angular/core';

import { Observable, filter, switchMap } from 'rxjs';

import { CustomIcon, IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { IconPosition, NotificationLayout } from '@fcom/ui-components';
import { GtmService } from '@fcom/common/gtm';
import {
  ElementActions,
  ElementTypes,
  GaContext,
  getCarTrawlerLang,
  getExpediaHotelLang,
  getExpediaHotelMDPCID,
  ENG_LANGUAGE_QUERY_PARAM,
} from '@fcom/common';
import { LanguageService } from '@fcom/ui-translate';
import { ConfigService } from '@fcom/core';
import { ExpediaHotelOrigin, ThirdPartyAncillariesProvider } from '@fcom/common/enums';

import { GTM_BOOKING_WIDGET_ELEMENT_IDS, mapToGaState } from '../../../manage-booking/constants';
import { ThirdPartyBookingWidgetConfig } from '../../interfaces/third-party-booking-widget.interface';

const THIRD_PARTY_WIDGET_CONFIG = {
  [ThirdPartyAncillariesProvider.EXPEDIA_HOTEL]: {
    providerId: 'hotels',
    companyName: 'expedia-hotels',
    poweredByTranslationKey: 'bookingWidgetTabs.hotels.poweredBy',
    ctaTranslationKey: 'bookingWidgetTabs.hotels.ctaText',
    aviosNotificationTranslationKey: 'bookingWidget.hotels.aviosNotification',
    logoIcon: CustomIcon.HOTELS_LOGO,
  },
  [ThirdPartyAncillariesProvider.CAR_TRAWLER_RENTAL]: {
    providerId: 'cartrawler-rental',
    companyName: 'cartrawler',
    poweredByTranslationKey: 'bookingWidgetTabs.carTrawler.poweredBy',
    ctaTranslationKey: 'bookingWidgetTabs.carTrawler.ctaText',
    aviosNotificationTranslationKey: 'bookingWidget.carTrawler.aviosNotification',
    logoIcon: CustomIcon.CARTRAWLER_LOGO,
  },
} as const;

@Component({
  selector: 'fin-third-party-booking-widget',
  templateUrl: './third-party-booking-widget.component.html',
  styleUrls: ['./third-party-booking-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ThirdPartyBookingWidgetComponent implements OnInit {
  private gtmService = inject(GtmService);
  private languageService = inject(LanguageService);
  private configService = inject(ConfigService);

  readonly type = input.required<ThirdPartyAncillariesProvider>();

  readonly CustomIcon = CustomIcon;
  readonly IconLibrary = IconLibrary;
  readonly IconPosition = IconPosition;
  readonly NotificationLayout = NotificationLayout;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  config: ThirdPartyBookingWidgetConfig;
  redirectUrl: string;
  providerLocale: string;
  notificationContentUrl$: Observable<string>;
  nonMatchingProviderLocale: boolean;
  isProviderInMatchingLanguage: boolean;

  ngOnInit(): void {
    this.config = THIRD_PARTY_WIDGET_CONFIG[this.type()];

    this.setupProviderSpecificData();

    // Set computed property for template use
    this.nonMatchingProviderLocale = !this.isProviderInMatchingLanguage;
  }

  trackClick(): void {
    this.gtmService.trackElement(
      GTM_BOOKING_WIDGET_ELEMENT_IDS[this.type()],
      GaContext.THIRD_PARTY_ANCILLARY,
      ElementTypes.LINK,
      mapToGaState[this.type()],
      ElementActions.CLICK
    );
  }

  private setupProviderSpecificData(): void {
    if (this.type() === ThirdPartyAncillariesProvider.CAR_TRAWLER_RENTAL) {
      this.setupCarTrawlerData();
      return;
    }

    this.setupHotelsData();
  }

  private setupCarTrawlerData(): void {
    const { apiUrl, clientId } = this.configService.cfg.carRental;
    const carTrawlerLocale = getCarTrawlerLang(
      ThirdPartyAncillariesProvider.CAR_TRAWLER_RENTAL,
      this.languageService.langValue
    );
    this.redirectUrl = `${apiUrl}/${carTrawlerLocale}/home?clientId=${clientId}`;
    this.providerLocale = carTrawlerLocale;

    const carTrawlerLang = carTrawlerLocale.split('-')[0];
    this.isProviderInMatchingLanguage = this.languageService.langKeyValue === carTrawlerLang;

    this.notificationContentUrl$ = this.languageService.langKey.pipe(
      switchMap((currentLang) => this.getNotificationFragmentUrl(currentLang)),
      filter(Boolean)
    );
  }

  private setupHotelsData(): void {
    const hotelLangConfig = getExpediaHotelLang(this.languageService.langValue);
    const mdpcid = getExpediaHotelMDPCID(this.languageService.langValue, ExpediaHotelOrigin.WIDGET_HOTEL_TAB);
    const baseUrl = `https://${hotelLangConfig.urlPrefix}.hotels.finnair.com`;
    const queryParams = [`mdpcid=${mdpcid}`, hotelLangConfig.additionalQueryParam || ''].filter(Boolean).join('&');
    this.redirectUrl = `${baseUrl}?${queryParams}`;
    this.providerLocale = hotelLangConfig.urlPrefix;

    const isHotelsInEnglish =
      hotelLangConfig.urlPrefix === 'uk' ||
      hotelLangConfig.urlPrefix === 'ie' ||
      (hotelLangConfig.additionalQueryParam?.includes(ENG_LANGUAGE_QUERY_PARAM) ?? false);
    const isCurrentLocaleEnglish = this.languageService.langKeyValue === 'en';
    this.isProviderInMatchingLanguage = isHotelsInEnglish === isCurrentLocaleEnglish;
  }

  private getNotificationFragmentUrl(currentLang: string): Observable<string> {
    const fragmentUrlKey =
      currentLang === this.providerLocale
        ? 'fragments.bookingWidgetCarTrawlerDefault.url'
        : 'fragments.bookingWidgetCarTrawlerToEnglish.url';

    return this.languageService.translate(fragmentUrlKey);
  }
}
